import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { WithdrawalsQueryKeys } from '../../queryKeys';
import { fetchWithdrawalById } from './service';
import { UseWithdrawalByIdQueryKey } from './props';
import { GenericError, Withdraw } from '@starsoft/common/models';
import { WithdrawFindOneRequestDto } from '../../../../dto/withdrawals/withdraw-find-one-request.dto';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to fetch withdrawal data by ID using React Query
 *
 * @param {WithdrawFindOneRequestDto} params - Object containing withdrawal ID
 * @param {string} params.id - The ID of the withdrawal to fetch
 *
 * @returns {Object} Returns an object containing:
 * @returns {Function} refetch - Function to manually trigger a refetch
 * @returns {AxiosError<GenericError>} error - Any error that occurred during the fetch
 * @returns {Withdraw} withdraw - The fetched withdrawal data
 * @returns {boolean} isLoading - Loading state indicator
 */
export function useWithdrawalById({ id }: WithdrawFindOneRequestDto) {
  // Get axios instance for API calls
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Construct query key for React Query cache management
  const queryKey: UseWithdrawalByIdQueryKey = [
    WithdrawalsQueryKeys.ById,
    { id },
  ];

  // Execute the query using React Query
  const { isPending, error, data, refetch, fetchStatus } = useQuery<
    Withdraw,
    AxiosError<GenericError>,
    Withdraw,
    UseWithdrawalByIdQueryKey
  >({
    queryKey,
    enabled: !!id, // Only run query if ID exists
    staleTime: 5 * 1000, // Consider data stale after 5 seconds
    queryFn: queryContext => fetchWithdrawalById(queryContext, axiosInstance),
  });

  // Return relevant data and states
  return {
    refetch,
    error: error,
    withdraw: data,
    isLoading: isPending && fetchStatus !== 'idle',
  };
}
